import { DataViewTypes } from '@mode/shared/contract-common';
import { errorGif } from '@mode/shared/ui-images';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { annotateSqlWithSyntaxError, formatBytes, isPresent } from '@mode/shared/util-js';
import { DataViewFacade } from '@mode/switch-views/contract';
import * as angular from 'angular';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { MixableBaseController } from '~/common/component';
import { AppStore } from '~/types/main.types';
import { transientSelectedQueryTokenSelector } from '../../../../reducers/selectors';
import { ReportRunDataService } from '../../../../services/report-run-service';
import { DatasetFacade } from '@mode/dataset/contract';

class ReportQueryResultController extends MixableBaseController implements angular.IOnInit {
  static $inject = [
    '$stateParams',
    'DataViewFacade',
    'ReportRunDataService',
    '$sce',
    'RxStoreService',
    'FeatureFlagsFacade',
    'DatasetFacade',
  ];

  queryToken: string;

  bytesSize$: Observable<string>;
  datasetResult$: Observable<DataViewTypes.DatasetResult>;
  executionTime$: Observable<string>;
  isPurged$: Observable<boolean>;
  lastSuccessfulRunAt$: Observable<string>;
  queryRunStateIsFailed$: Observable<boolean>;
  queryToken$: Observable<string>;
  queryTokenBinding$: BehaviorSubject<string>;
  reportRunState$: Observable<{
    errorMessage: string;
    errorLineHtml: string | any;
  }>;

  readonly header = 'This data was removed';
  readonly subtext = 'This was a result of your Workspace’s data retention policy.';
  readonly icon = 'illo-air-ballon';

  public switchViewToken$: Observable<string>;
  public showLegacyFlamingoGrid$: Observable<boolean>;
  public showFlamingoGrid$: Observable<boolean>;
  public isSheetDataset$: Observable<boolean>;

  public errorImage: string;

  constructor(
    private $stateParams,
    private dataViewFacade: DataViewFacade,
    private reportRunDataService: ReportRunDataService,
    private $sce: angular.ISCEService,
    private rxStore: AppStore,
    private featureFlagsFacade: FeatureFlagsFacade,
    private datasetFacade: DatasetFacade
  ) {
    super();
  }

  $onInit(): void {
    this.queryToken = this.$stateParams['queryId'];
    this.errorImage = errorGif;

    this.dataViewFacade.queryViewDetailsResults(this.queryToken);
    this.dataViewFacade.queryViewDetailsResultsAngularJs(this.queryToken);
    this.datasetResult$ = this.dataViewFacade
      .getDatasetResultByQueryNoEmpty(this.queryToken)
      .pipe(takeUntil(this.destroyed$));

    this.queryRunStateIsFailed$ = this.datasetResult$.pipe(
      map((datasetResult) => datasetResult.queryRunState === DataViewTypes.QueryRunStatus.Failed)
    );

    this.queryToken$ =
      this.queryToken == null ? this.rxStore.select(transientSelectedQueryTokenSelector) : of(this.queryToken);

    this.switchViewToken$ = this.queryToken$.pipe(
      filter(isPresent),
      switchMap((queryToken) => this.dataViewFacade.createSwitchViewTokenByQueryToken(queryToken))
    );

    this.isSheetDataset$ = this.datasetResult$.pipe(
      map(({ producerType }) => this.datasetFacade.isSheetDataset(producerType))
    );

    this.isPurged$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.getIsPurgedBySwitchViewToken(switchViewToken))
    );
    this.executionTime$ = combineLatest([
      this.isSheetDataset$,
      this.switchViewToken$.pipe(
        switchMap((switchViewToken) => this.dataViewFacade.getExecutionTimeBySwitchViewToken(switchViewToken))
      ),
    ]).pipe(map(([isSheetDataset, executionTime]) => (isSheetDataset ? '' : executionTime)));

    this.bytesSize$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) =>
        this.dataViewFacade
          .getBytesSizeBySwitchViewToken(switchViewToken)
          .pipe(map((bytesSize) => formatBytes(bytesSize)))
      )
    );

    this.lastSuccessfulRunAt$ = this.switchViewToken$.pipe(
      switchMap((switchViewToken) => this.dataViewFacade.getDatasetResultCompletedAtBySwitchViewToken(switchViewToken))
    );

    this.reportRunState$ = this.dataViewFacade.getRenderedQueryByQueryToken(this.queryToken).pipe(
      withLatestFrom(this.dataViewFacade.getErrorByQueryToken(this.queryToken)),
      map(([renderedQuery, errorMessage]) => {
        const errorLineHtml = annotateSqlWithSyntaxError({
          state: DataViewTypes.QueryRunStatus.Failed,
          errorMessage: errorMessage,
          renderedSource: renderedQuery,
        });

        return {
          errorMessage: errorMessage ?? "Sorry, the dataset can't be displayed—there's an error in the source data",
          errorLineHtml: this.$sce.trustAsHtml(errorLineHtml) || '',
        };
      }),
      takeUntil(this.destroyed$)
    );
  }
}

export const ID = 'reportQueryResult';

export const reportQueryResult: angular.IComponentOptions = {
  template: `
    <div ng-if="(ctrl.isPurged$ | async:this) === true" class="purged-error-state">
      <mode-data-retention-no-data
        dataRetentionNoData
        [icon]="ctrl.icon"
        [header]="ctrl.header"
        [subtext]="ctrl.subtext"
      ></mode-data-retention-no-data>
    </div>
    <div class="editor-error" ng-if="(ctrl.queryRunStateIsFailed$ | async:this)">
      <img ng-src="{{ctrl.errorImage}}" class="error-gif">
      <h3>Looks like something went wrong with your query.</h3>
      <pre>{{ (ctrl.reportRunState$ | async:this).errorMessage }}</pre>
      <div class="error-lines" ng-bind-html="(ctrl.reportRunState$ | async:this).errorLineHtml"></div>
    </div>
    <mode-sql-no-results-message
      ng-if="(ctrl.datasetResult$ | async:this).rowCount === 0 && (ctrl.isPurged$ | async:this) === false"
    ></mode-sql-no-results-message>
    <mode-flamingo-grid-ng
      ng-if="(ctrl.isPurged$ | async:this) === false"
      [csv-link]="(ctrl.datasetResult$ | async:this).csvUrl"
      [execution-time]="(ctrl.executionTime$ | async:this)"
      [last-successful-label]="(ctrl.isSheetDataset$ | async:this) ? 'Synced' : 'Run'"
      [last-successful-run-at]="(ctrl.lastSuccessfulRunAt$ | async:this)"
      [query-token]="ctrl.queryToken"
      [size]="(ctrl.bytesSize$ | async:this)"
      [size-grid-to-fit]="true"
      >
    </mode-flamingo-grid-ng>

  `,
  controller: ReportQueryResultController,
  controllerAs: 'ctrl',
};
